import React, { PropsWithChildren, useMemo, useState } from 'react';
import cn from 'classnames';
import Panel from '@views/Onboarding/panels/Panel';
import CopySection, { FinishStepButton } from '@views/Onboarding/panels/CopySection';
import Avatar from '@views/Contact/ProfileEditor/Avatar';
import { useImageLoadingCallback } from '@repo/hooks';
import { Select, SelectOption } from '@repo/components/forms';
import styles from './ProfileForm.module.scss';
import { Birthday, Profile } from '..';
import buildFormData from '@repo/utils/helpers/buildFormData';
import { apiRequest } from '@repo/utils/helpers/api';
import { dayOptions, fullMonths, getOption, monthOptions, yearOptions } from './options';
import analytics from '@repo/utils/helpers/analytics';

const FormField = ({
  className,
  label,
  children,
  optional,
}: PropsWithChildren<{ className?: string; label: string; optional?: boolean }>) => (
  <div className={className}>
    <label className="form-label">
      {label} {optional && <span className="optional">(optional)</span>}
    </label>
    {children}
  </div>
);

const NumberSelect = ({
  options,
  value,
  onChange,
}: {
  options: SelectOption<number>[];
  value: SelectOption<number> | undefined;
  onChange: (option: SelectOption<number>) => void;
}) => (
  <Select
    className={styles.select}
    options={options}
    value={value}
    onChange={onChange}
    isSearchable
    noMenuWrap
    maxMenuHeight={150}
  />
);

export default function ProfileForm({
  personID,
  defaultProfile,
  finishStep,
}: {
  personID: string;
  defaultProfile: Profile;
  finishStep: () => void;
}) {
  const [personProfile, setPersonProfile] = useState<Profile>({ ...defaultProfile });
  const [avatarImgToUpload, setAvatarImgToUpload] = useState<null | File>(null);
  const [avatarOptionsLoaded, setAvatarOptionsLoaded] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingFailed, setSavingFailed] = useState(false);
  const filteredDayOptions = useMemo(() => {
    if (personProfile.birthday?.month === 2) {
      const date = new Date(personProfile.birthday?.year || 2000, 2, 0);
      return dayOptions.slice(0, date.getDate());
    }

    if (personProfile.birthday?.month && !fullMonths.has(personProfile.birthday?.month)) {
      return dayOptions.slice(0, 30);
    }

    return dayOptions;
  }, [personProfile.birthday?.month, personProfile.birthday?.year]);

  useImageLoadingCallback(personProfile.avatar_options || [], () => setAvatarOptionsLoaded(true));

  const updateProfileField = (field: 'first_name' | 'last_name') => (e) => {
    setPersonProfile({ ...personProfile, [field]: e.target.value });
  };

  const updateBirthday = (birthday: Birthday) => {
    setPersonProfile({
      ...personProfile,
      birthday: { ...(personProfile.birthday || {}), ...birthday },
    });
  };

  const updateBirthdayMonth = ({ value }: SelectOption<number>) => {
    if (personProfile.birthday?.day === 31 && !fullMonths.has(value) && value !== 2) {
      return updateBirthday({ month: value, day: 30 });
    }

    if (value === 2 && personProfile.birthday?.day > 28) {
      // 2000 is a leap year
      const date = new Date(personProfile.birthday?.year || 2000, 2, 0);
      return updateBirthday({ month: value, day: date.getDate() });
    }

    updateBirthday({ month: value });
  };

  const updateBirthdayYear = ({ value }: SelectOption<number>) => {
    if (personProfile.birthday?.month === 2 && personProfile.birthday?.day === 29) {
      const date = new Date(value, 2, 0);
      return updateBirthday({ year: value, day: date.getDate() });
    }

    updateBirthday({ year: value });
  };

  const setAvatar = (avatar_url, fileToUpload) => {
    setPersonProfile({ ...personProfile, avatar_url });
    setAvatarImgToUpload(fileToUpload);
  };

  const savePersonProfile = async (
    profile: Omit<Profile, 'avatar_url'> & { avatar_url: File | string },
  ) => {
    try {
      return await apiRequest<Profile>(
        'PATCH',
        `/public-data/${personID}/`,
        buildFormData(profile),
        { apiVersion: 'v2', noAuthorization: true },
      );
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const approveProfileAndGoToSignup = async () => {
    setSavingProfile(true);
    const personProfileUpdated = await savePersonProfile({
      ...personProfile,
      avatar_url: avatarImgToUpload || personProfile.avatar_url,
    });

    if (personProfileUpdated) {
      analytics.track('Birthday info submitted');
      finishStep();
    } else {
      setSavingProfile(false);
      setSavingFailed(true);
    }
  };

  return (
    <Panel className={styles.panel}>
      <CopySection>
        <h2>Add your birthday</h2>
        <p>
          Confirm your name and enter your birthday to keep all your friends who use Clay up to
          date.
        </p>
      </CopySection>
      <div className={styles.profileForm}>
        <Avatar
          className={cn(styles.avatarEditor, avatarOptionsLoaded && styles.loaded)}
          value={personProfile.avatar_url || null}
          avatar={{
            key: 'avatar_url',
            type: 'gallery',
            options: personProfile.avatar_options || [],
            value: personProfile.avatar_url || null,
          }}
          onChange={setAvatar}
          displayName={`${personProfile.first_name} ${personProfile.last_name}`}
          initials={personProfile.first_name[0] || ''}
        />
        <FormField className={styles.span3} label="First Name">
          <input
            className={cn('form-input')}
            type="text"
            value={personProfile.first_name}
            onChange={updateProfileField('first_name')}
          />
        </FormField>
        <FormField className={styles.span3} label="Last Name">
          <input
            className={cn('form-input')}
            type="text"
            value={personProfile.last_name}
            onChange={updateProfileField('last_name')}
          />
        </FormField>
        <FormField className={styles.span2} label="Day">
          <NumberSelect
            options={filteredDayOptions}
            value={getOption(dayOptions, personProfile.birthday?.day)}
            onChange={({ value }) => updateBirthday({ day: value })}
          />
        </FormField>
        <FormField className={styles.span2} label="Month">
          <NumberSelect
            options={monthOptions}
            value={getOption(monthOptions, personProfile.birthday?.month)}
            onChange={updateBirthdayMonth}
          />
        </FormField>
        <FormField className={styles.span2} label="Year">
          <NumberSelect
            options={yearOptions}
            value={getOption(yearOptions, personProfile.birthday?.year)}
            onChange={updateBirthdayYear}
          />
        </FormField>
      </div>
      <FinishStepButton
        onClick={approveProfileAndGoToSignup}
        disabled={!personProfile.birthday?.day || !personProfile.birthday?.month || savingProfile}
        disabledMsg="Please select a day and month to continue"
        loading={savingProfile}
        errorMsg={savingFailed && 'Something went wrong, please try again'}
      >
        Continue
      </FinishStepButton>
    </Panel>
  );
}
